import React, { useEffect, useState } from 'react';
import { Select } from 'antd';
import moment from 'moment';
import { useFormContext } from 'react-hook-form';
import FilterSearchBar from 'components/shared/FilterSearchBar';
import FormDateRange from 'components/shared/BWFormControl/FormDateRange';
import FormSelect from 'components/shared/BWFormControl/FormSelect';
import FormInput from 'components/shared/BWFormControl/FormInput';
import useGetOptions, { optionType } from 'hooks/useGetOptions';
import { getBankReportOptions } from 'services/report';
import { showToast } from 'utils/helpers';

const periods = [
  { value: 'month', label: 'Tháng này' },
  { value: 'previous_month', label: 'Tháng trước' },
  { value: 'quarter', label: 'Quý này' },
  { value: 'year', label: 'Năm nay' },
  { value: 'custom', label: 'Tùy chọn' },
];

export const bankReportPeriod = (period, now = moment()) => {
  const date = moment(now);
  if (period === 'previous_month') date.subtract(1, 'month');
  const unit = period === 'previous_month' ? 'month' : period;
  return { accounting_date_from: date.clone().startOf(unit).format('YYYY-MM-DD'),
    accounting_date_to: date.clone().endOf(unit).format('YYYY-MM-DD') };
};

export const withBankReportPeriod = (code, values) => {
  if (!['BANK-001', 'BANK-005'].includes(code)) return values;
  const defaults = bankReportPeriod('month');
  return { ...values, accounting_date_from: values.accounting_date_from || defaults.accounting_date_from,
    accounting_date_to: values.accounting_date_to || defaults.accounting_date_to };
};

const BankReportFilters = ({ reportCode, onSubmit, onClear }) => {
  const methods = useFormContext();
  const [companyId, businessId, from, to] = methods.watch(['company_id', 'business_id', 'accounting_date_from', 'accounting_date_to']);
  const companyOptions = useGetOptions(optionType.company, { isHasOptionAll: true });
  const businessOptions = useGetOptions(optionType.business, { isHasOptionAll: true, params: { company_id: companyId || 0, is_active: 1 } });
  const storeOptions = useGetOptions(optionType.storeByBusiness, { isHasOptionAll: true, params: { parent_id: businessId || 0, is_active: 1 } });
  const [options, setOptions] = useState({ accounts: [], banks: [] });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let active = true;
    setOptions({ accounts: [], banks: [] });
    setLoading(true);
    getBankReportOptions(reportCode, { company_id: companyId, business_id: businessId })
      .then(response => { if (active) setOptions(response?.data || response); })
      .catch(error => { if (active) showToast.error(error?.message || 'Không tải được danh mục ngân hàng.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [reportCode, companyId, businessId]);
  const clearDependent = (companyChanged) => {
    if (companyChanged) methods.setValue('business_id', 0);
    methods.setValue('store_id', 0);
    methods.setValue('accounting_account_code', null);
    methods.setValue('bank_account_id', null);
  };
  const format = value => moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true).format('YYYY-MM-DD');
  const selectedPeriod = periods.filter(row => row.value !== 'custom').find(row => {
    const range = bankReportPeriod(row.value);
    return range.accounting_date_from === format(from) && range.accounting_date_to === format(to);
  })?.value || 'custom';

  return <div className='bc_bank_report_filters'><FilterSearchBar title='Tham số báo cáo' compact colSize={3} onSubmit={onSubmit} onClear={onClear} actions={[
    { title: 'Kỳ báo cáo', component: <Select aria-label='Kỳ báo cáo' value={selectedPeriod} options={periods}
      style={{ width: '100%' }} onChange={value => {
        if (value !== 'custom') Object.entries(bankReportPeriod(value)).forEach(([field, date]) => methods.setValue(field, date));
      }} /> },
    { title: 'Từ ngày – Đến ngày', component: <FormDateRange fieldStart='accounting_date_from' fieldEnd='accounting_date_to'
      format='DD/MM/YYYY' placeholder={['Từ ngày', 'Đến ngày']} allowClear={false} /> },
    { title: 'Tài khoản kế toán', component: <FormSelect field='accounting_account_code' aria-label='Tài khoản kế toán'
      list={options.accounts || []} allowClear placeholder='Tất cả tài khoản' loading={loading} /> },
    { title: 'Tài khoản ngân hàng', component: <FormSelect field='bank_account_id' aria-label='Tài khoản ngân hàng'
      list={options.banks || []} allowClear placeholder='Tất cả tài khoản ngân hàng' loading={loading} /> },
    { title: 'Công ty', component: <FormSelect field='company_id' list={companyOptions} onChange={() => clearDependent(true)} /> },
    { title: 'Chi nhánh', component: <FormSelect field='business_id' list={businessOptions} onChange={() => clearDependent(false)} /> },
    { title: 'Cửa hàng', component: <FormSelect field='store_id' list={storeOptions} /> },
    { title: reportCode === 'BANK-005' ? 'Tìm tài khoản ngân hàng' : 'Từ khóa', component: <FormInput field='keyword'
      placeholder={reportCode === 'BANK-005' ? 'Số tài khoản, ngân hàng, chi nhánh' : 'Số chứng từ, diễn giải, đối tượng'} /> },
    { title: 'Số chứng từ', component: <FormInput field='document_no' placeholder='Nhập số chứng từ' />, hidden: reportCode === 'BANK-005' },
  ]} /></div>;
};

export default BankReportFilters;
